/**
 * Settings, in sections: a menu on the left (on top on a phone), one section
 * at a time. Desktop-only sections (Claude Desktop, updates) appear only there.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'radix-ui';
import {
  Bell,
  Info,
  Keyboard,
  LayoutTemplate,
  Puzzle,
  ShieldCheck,
  Sparkles,
  SlidersHorizontal,
  UserRound,
} from 'lucide-react';
import { PluginSettings, TemplateSettings } from './SettingsModules';
import { LANGUAGES, type Language } from '../i18n';
import { useOpenedStore } from '../db/context';
import { useMenuEditor } from '../lib/sidebarMenu';
import { useUi, type SettingsTab, type Theme } from '../state/ui';
import { formatKeys, SHORTCUT_KEYS } from '../shortcuts';
import { Dialog } from './Dialog';
import { AccountSettings } from '../sync/AccountSettings';
import { ManagementSettings } from '../sync/ManagementSettings';
import { useRole } from '../sync/useRole';
import { NotificationSettingsForm } from '../notifications/NotificationSettingsForm';
import { UpdateSettings } from '../desktop/updates';
import { McpSettings } from '../mcp/McpSettings';
import { DataSettings } from './DataSettings';
import { AiSettings } from '../ai/AiSettings';
import { ActivityLog } from '../ai/ActivityLog';

const select = 'rounded-lg border border-border bg-bg px-2 py-1 text-sm';

function Section({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: ReactNode;
}) {
  return (
    <section className="mb-6 last:mb-0">
      <h3 className="text-base font-semibold">{title}</h3>
      {intro && <p className="mt-0.5 text-xs text-fg-muted">{intro}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function SettingsDialog() {
  const { t } = useTranslation();
  const { backend } = useOpenedStore();
  const { settingsOpen, settingsTab, set } = useUi();
  const desktop = backend === 'desktop';
  const role = useRole();

  const tabs: { id: SettingsTab; label: string; icon: ReactNode }[] = [
    {
      id: 'general',
      label: t('settings.tabs.general'),
      icon: <SlidersHorizontal className="size-4" />,
    },
    { id: 'account', label: t('settings.tabs.account'), icon: <UserRound className="size-4" /> },
    {
      id: 'notifications',
      label: t('settings.tabs.notifications'),
      icon: <Bell className="size-4" />,
    },
    { id: 'ai', label: t('settings.tabs.ai'), icon: <Sparkles className="size-4" /> },
    {
      id: 'templates',
      label: t('settings.tabs.templates'),
      icon: <LayoutTemplate className="size-4" />,
    },
    { id: 'plugins', label: t('settings.tabs.plugins'), icon: <Puzzle className="size-4" /> },
    ...(role !== null
      ? [
          {
            id: 'management' as const,
            label: t('settings.tabs.management'),
            icon: <ShieldCheck className="size-4" />,
          },
        ]
      : []),
    { id: 'shortcuts', label: t('settings.tabs.shortcuts'), icon: <Keyboard className="size-4" /> },
    { id: 'about', label: t('settings.tabs.about'), icon: <Info className="size-4" /> },
  ];
  // Claude Desktop (MCP) lives under AI now; an old 'mcp' tab opens there.
  const wanted = settingsTab === 'mcp' ? 'ai' : settingsTab;
  const current = tabs.some((x) => x.id === wanted) ? wanted : 'general';

  return (
    <Dialog
      open={settingsOpen}
      onOpenChange={(o) => set({ settingsOpen: o })}
      title={t('settings.title')}
      wide
    >
      <Tabs.Root
        value={current}
        onValueChange={(v) => set({ settingsTab: v as SettingsTab })}
        orientation="vertical"
        className="flex min-h-0 flex-col gap-4 sm:h-[min(36rem,calc(84vh-7.5rem))] sm:flex-row"
      >
        <Tabs.List
          aria-label={t('settings.title')}
          className="-mx-1 flex shrink-0 gap-1 overflow-x-auto px-1 pb-1 sm:mx-0 sm:w-44 sm:flex-col sm:overflow-visible sm:border-r sm:border-border sm:pr-3"
        >
          {tabs.map((x) => (
            <Tabs.Trigger
              key={x.id}
              value={x.id}
              className="flex shrink-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm whitespace-nowrap text-fg-muted hover:bg-bg-hover hover:text-fg data-[state=active]:bg-accent-soft data-[state=active]:font-medium data-[state=active]:text-fg"
            >
              {x.icon}
              {x.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto pr-1">
          <Tabs.Content value="general">
            <General />
          </Tabs.Content>
          <Tabs.Content value="account">
            <Section title={t('sync.title')} intro={t('settings.intro.account')}>
              <AccountSettings />
            </Section>
          </Tabs.Content>
          <Tabs.Content value="notifications">
            <Section
              title={t('notifications.settings.title')}
              intro={t('settings.intro.notifications')}
            >
              <NotificationSettingsForm />
            </Section>
          </Tabs.Content>
          <Tabs.Content value="ai">
            <Section title={t('ai.title')} intro={t('settings.intro.ai')}>
              <AiSettings />
            </Section>
            {desktop && (
              <Section title={t('mcp.title')}>
                <McpSettings />
              </Section>
            )}
            <Section title={t('ai.activity.title')} intro={t('settings.intro.activity')}>
              <ActivityLog />
            </Section>
          </Tabs.Content>
          <Tabs.Content value="templates">
            <Section title={t('settings.tabs.templates')} intro={t('settings.intro.templates')}>
              <TemplateSettings />
            </Section>
          </Tabs.Content>
          <Tabs.Content value="plugins">
            <Section title={t('settings.tabs.plugins')} intro={t('settings.intro.plugins')}>
              <PluginSettings />
            </Section>
          </Tabs.Content>
          {role !== null && (
            <Tabs.Content value="management">
              <Section title={t('settings.tabs.management')} intro={t('settings.intro.management')}>
                <ManagementSettings />
              </Section>
            </Tabs.Content>
          )}
          <Tabs.Content value="shortcuts">
            <Section title={t('settings.shortcuts')}>
              <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
                {Object.entries(SHORTCUT_KEYS).map(([id, keys]) => (
                  <div key={id} className="contents">
                    <dt className="text-fg-muted">{t(`shortcuts.${id}`)}</dt>
                    <dd>
                      <kbd className="rounded-md border border-border px-1.5 text-xs">
                        {formatKeys(keys)}
                      </kbd>
                    </dd>
                  </div>
                ))}
              </dl>
            </Section>
          </Tabs.Content>
          <Tabs.Content value="about">
            <About />
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </Dialog>
  );
}

function General() {
  const { t } = useTranslation();
  const { set, theme, language, fullWidthDefault } = useUi();
  return (
    <Section title={t('settings.tabs.general')}>
      <div className="grid grid-cols-[8rem_1fr] items-center gap-x-4 gap-y-3 text-sm">
        <label htmlFor="set-lang">{t('settings.language')}</label>
        <select
          id="set-lang"
          className={select}
          value={language}
          onChange={(e) => set({ language: e.target.value as Language })}
        >
          {Object.entries(LANGUAGES).map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>

        <label htmlFor="set-theme">{t('settings.theme')}</label>
        <select
          id="set-theme"
          className={select}
          value={theme}
          onChange={(e) => set({ theme: e.target.value as Theme })}
        >
          <option value="system">{t('settings.themeSystem')}</option>
          <option value="light">{t('settings.themeLight')}</option>
          <option value="dark">{t('settings.themeDark')}</option>
        </select>

        <label htmlFor="set-width">{t('settings.width')}</label>
        <label className="flex items-center gap-2">
          <input
            id="set-width"
            type="checkbox"
            checked={fullWidthDefault}
            onChange={(e) => set({ fullWidthDefault: e.target.checked })}
          />
          {t('settings.fullWidthDefault')}
        </label>

        <span>{t('sidebarMenu.settingsLabel')}</span>
        <button
          type="button"
          onClick={() => {
            set({ settingsOpen: false });
            useMenuEditor.getState().setOpen(true);
          }}
          className="justify-self-start rounded-full border border-border px-3 py-1 text-sm hover:bg-bg-hover"
        >
          {t('sidebarMenu.settingsButton')}
        </button>
      </div>
    </Section>
  );
}

function About() {
  const { t } = useTranslation();
  const { store, backend } = useOpenedStore();
  return (
    <>
      {backend === 'desktop' && (
        <Section title={t('updates.title')}>
          <UpdateSettings />
        </Section>
      )}
      <Section title={t('settings.thisDevice')}>
        <div className="grid grid-cols-[8rem_1fr] items-center gap-x-4 gap-y-3 text-sm">
          <span>{t('settings.storage')}</span>
          <span className="text-fg-muted">
            {backend === 'desktop' ? t('settings.storageDesktop') : t('settings.storageBrowser')}
          </span>
          <span>{t('settings.device')}</span>
          <code className="truncate text-xs text-fg-muted">{store.deviceId}</code>
        </div>
      </Section>
      <Section title={t('data.title')}>
        <DataSettings />
      </Section>
    </>
  );
}
